import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Package, AlertTriangle, DollarSign, Shield, Search,
  Eye, Edit, Ban, Trash2, CheckCircle, XCircle, RefreshCw,
  ChevronLeft, ChevronRight, Loader2, MoreVertical, History,
  TrendingUp, ShoppingCart, Flag, RotateCcw, UserX, AlertCircle,
  Zap, CreditCard, UserCheck, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Suspension reasons
const SUSPENSION_REASONS = [
  { id: 'fake_account', label: 'Fake Account', description: 'Account appears to be fake or automated' },
  { id: 'fraud', label: 'Fraudulent Activity', description: 'Engaged in fraudulent transactions or scams' },
  { id: 'misconduct', label: 'User Misconduct', description: 'Violated community guidelines or terms of service' },
  { id: 'harassment', label: 'Harassment', description: 'Harassing other users or sellers' },
  { id: 'counterfeit', label: 'Counterfeit Items', description: 'Selling counterfeit or misrepresented items' },
  { id: 'spam', label: 'Spam/Abuse', description: 'Spamming or abusing platform features' },
  { id: 'payment_issues', label: 'Payment Issues', description: 'Repeated payment failures or chargebacks' },
  { id: 'other', label: 'Other', description: 'Other violation (specify in notes)' }
];

// Deletion reasons
const DELETION_REASONS = [
  { id: 'fake_account', label: 'Fake Account', description: 'Confirmed fake or bot account' },
  { id: 'duplicate', label: 'Duplicate Account', description: 'User has multiple accounts' },
  { id: 'request', label: 'User Request', description: 'User requested account deletion' },
  { id: 'severe_violation', label: 'Severe Violation', description: 'Severe terms of service violation' },
  { id: 'legal', label: 'Legal Requirement', description: 'Required by law or legal process' },
  { id: 'other', label: 'Other', description: 'Other reason (specify in notes)' }
];

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-zinc-400 text-sm">{title}</span>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value?.toLocaleString() || 0}</p>
    {subtext && <p className="text-xs text-zinc-500 mt-1">{subtext}</p>}
  </div>
);

// Stripe Sync Section Component
const StripeSyncSection = ({ syncLoading, syncResult, onSync }) => {
  // Build result stats
  let statsBlocks = [];
  if (syncResult) {
    statsBlocks.push({ value: syncResult.total_stripe_subscriptions, label: 'Stripe Subscriptions', color: 'text-purple-400' });
    statsBlocks.push({ value: syncResult.users_updated, label: 'Users Updated', color: 'text-emerald-400' });
    statsBlocks.push({ value: syncResult.users_not_found, label: 'Not in DB', color: 'text-amber-400' });
    statsBlocks.push({ value: syncResult.errors?.length || 0, label: 'Errors', color: 'text-red-400' });
  }

  // Build user lists
  let updatedList = [];
  let notFoundList = [];
  if (syncResult?.updated_users) {
    for (let i = 0; i < syncResult.updated_users.length; i++) {
      const u = syncResult.updated_users[i];
      updatedList.push({ key: i, text: u.email + ' (' + u.status + ')' });
    }
  }
  if (syncResult?.not_found_customers) {
    for (let i = 0; i < syncResult.not_found_customers.length; i++) {
      const c = syncResult.not_found_customers[i];
      notFoundList.push({ key: i, text: c.email + ' (' + c.status + ')' });
    }
  }

  return (
    <div className="mt-8 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl p-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <CreditCard className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Stripe Subscription Sync</h3>
            <p className="text-sm text-zinc-400">Sync active Stripe subscribers with database to grant Gold access</p>
          </div>
        </div>
        
        <Button onClick={onSync} disabled={syncLoading} className="bg-purple-600 hover:bg-purple-500 text-white px-6" data-testid="sync-stripe-btn">
          {syncLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {syncLoading ? 'Syncing...' : 'Sync Stripe Subscribers'}
        </Button>
      </div>
      
      {syncResult && (
        <div className="mt-4 p-4 bg-zinc-900/50 rounded-lg border border-zinc-700">
          <h4 className="text-sm font-semibold text-white mb-3">Sync Results</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {statsBlocks.map((s, idx) => (
              <div key={idx} className="text-center p-2 bg-zinc-800 rounded">
                <p className={'text-2xl font-bold ' + s.color}>{s.value}</p>
                <p className="text-zinc-500 text-xs">{s.label}</p>
              </div>
            ))}
          </div>
          
          {updatedList.length > 0 && (
            <div className="mt-4">
              <h5 className="text-xs font-semibold text-emerald-400 mb-2">Users Granted Gold Access:</h5>
              <div className="flex flex-wrap gap-2">
                {updatedList.map(item => (
                  <span key={item.key} className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">{item.text}</span>
                ))}
              </div>
            </div>
          )}
          
          {notFoundList.length > 0 && (
            <div className="mt-4">
              <h5 className="text-xs font-semibold text-amber-400 mb-2">Active Subscribers Not in DB:</h5>
              <div className="flex flex-wrap gap-2">
                {notFoundList.map(item => (
                  <span key={item.key} className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">{item.text}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// User Row Component
const UserRow = ({ user, onView, onEdit, onSuspend, onDelete }) => (
  <tr className="border-b border-zinc-800 hover:bg-zinc-800/30">
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center text-sm font-medium text-white">
          {user.name?.charAt(0) || '?'}
        </div>
        <div>
          <p className="text-white font-medium">{user.name || 'Unknown'}</p>
          <p className="text-xs text-zinc-500">{user.email}</p>
        </div>
      </div>
    </td>
    <td className="py-3 px-4">
      <span className="text-zinc-400 text-sm">@{user.username || 'N/A'}</span>
    </td>
    <td className="py-3 px-4">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        user.is_suspended 
          ? 'bg-red-500/20 text-red-400' 
          : user.is_seller 
            ? 'bg-emerald-500/20 text-emerald-400'
            : 'bg-zinc-700 text-zinc-300'
      }`}>
        {user.is_suspended ? 'Suspended' : user.is_seller ? 'Seller' : 'Buyer'}
      </span>
    </td>
    <td className="py-3 px-4 text-zinc-400 text-sm">
      {user.membership_status || 'Free'}
    </td>
    <td className="py-3 px-4 text-zinc-400 text-sm">
      {user.total_sales || 0} sales
    </td>
    <td className="py-3 px-4">
      <div className="flex items-center gap-1">
        <button onClick={() => onView(user)} className="p-1.5 hover:bg-zinc-700 rounded" title="View">
          <Eye className="w-4 h-4 text-zinc-400" />
        </button>
        <button onClick={() => onEdit(user)} className="p-1.5 hover:bg-zinc-700 rounded" title="Edit">
          <Edit className="w-4 h-4 text-zinc-400" />
        </button>
        {user.is_suspended ? (
          <button onClick={() => onSuspend(user, false)} className="p-1.5 hover:bg-zinc-700 rounded" title="Unsuspend">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
          </button>
        ) : (
          <button onClick={() => onSuspend(user, true)} className="p-1.5 hover:bg-zinc-700 rounded" title="Suspend">
            <Ban className="w-4 h-4 text-amber-400" />
          </button>
        )}
        <button onClick={() => onDelete(user)} className="p-1.5 hover:bg-zinc-700 rounded" title="Delete">
          <Trash2 className="w-4 h-4 text-red-400" />
        </button>
      </div>
    </td>
  </tr>
);

// Listing Row Component
const ListingRow = ({ listing, onView, onEdit, onRemove, onRestore, onHardDelete }) => (
  <tr className="border-b border-zinc-800 hover:bg-zinc-800/30">
    <td className="py-3 px-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-zinc-800 rounded-lg overflow-hidden">
          {listing.images?.[0] ? (
            <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-600">
              <Package className="w-5 h-5" />
            </div>
          )}
        </div>
        <div>
          <p className="text-white font-medium">{listing.player_name}</p>
          <p className="text-xs text-zinc-500">{listing.year} {listing.set_name}</p>
        </div>
      </div>
    </td>
    <td className="py-3 px-4 text-amber-400 font-medium">
      ${listing.price?.toFixed(2)}
    </td>
    <td className="py-3 px-4">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        listing.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
        listing.status === 'sold' ? 'bg-blue-500/20 text-blue-400' :
        listing.status === 'removed' ? 'bg-red-500/20 text-red-400' :
        'bg-zinc-700 text-zinc-300'
      }`}>
        {listing.status}
      </span>
    </td>
    <td className="py-3 px-4 text-zinc-400 text-sm">
      {listing.seller?.name || 'Unknown'}
    </td>
    <td className="py-3 px-4">
      {listing.report_count > 0 && (
        <span className="flex items-center gap-1 text-red-400 text-sm">
          <Flag className="w-3 h-3" />
          {listing.report_count}
        </span>
      )}
    </td>
    <td className="py-3 px-4">
      <div className="flex items-center gap-1">
        <button onClick={() => onView(listing)} className="p-1.5 hover:bg-zinc-700 rounded" title="View">
          <Eye className="w-4 h-4 text-zinc-400" />
        </button>
        <button onClick={() => onEdit(listing)} className="p-1.5 hover:bg-zinc-700 rounded" title="Edit">
          <Edit className="w-4 h-4 text-zinc-400" />
        </button>
        {listing.status === 'removed' ? (
          <button onClick={() => onRestore(listing)} className="p-1.5 hover:bg-zinc-700 rounded" title="Restore">
            <RotateCcw className="w-4 h-4 text-emerald-400" />
          </button>
        ) : (
          <button onClick={() => onRemove(listing)} className="p-1.5 hover:bg-zinc-700 rounded" title="Remove">
            <XCircle className="w-4 h-4 text-red-400" />
          </button>
        )}
        <button onClick={() => onHardDelete(listing)} className="p-1.5 hover:bg-red-900/50 rounded" title="Permanently Delete">
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </td>
  </tr>
);

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Modal states
  const [suspendModal, setSuspendModal] = useState({ open: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [listingDeleteModal, setListingDeleteModal] = useState({ open: false, listing: null });
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Stripe Sync State
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  const token = localStorage.getItem('slab_token');
  const limit = 20;

  // Stripe Sync Function
  const handleStripeSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/sync-stripe-subscriptions`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncResult(data);
        
        if (data.users_updated > 0) {
          toast.success(`Synced ${data.users_updated} subscribers from Stripe!`);
        } else if (data.users_not_found > 0) {
          toast.warning(`Found ${data.total_stripe_subscriptions} Stripe subscriptions, but ${data.users_not_found} users not in DB`);
        } else {
          toast.info('Sync complete - no updates needed');
        }
        
        // Refresh stats and users
        fetchStats();
        if (activeTab === 'users') {
          fetchUsers();
        }
      } else {
        const error = await response.json();
        toast.error(`Sync failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Stripe sync error:', error);
      toast.error('Failed to sync with Stripe');
    } finally {
      setSyncLoading(false);
    }
  };

  // Check admin access
  useEffect(() => {
    if (user && !user.is_founder && !user.is_superuser && user.role !== 'admin' && user.role !== 'superuser' && user.membership_status !== 'Creator') {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit,
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      });
      
      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch listings
  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit,
        ...(search && { search }),
        ...(statusFilter !== 'all' && { status: statusFilter })
      });
      
      const response = await fetch(`${API_URL}/api/admin/listings?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setListings(data.listings);
        setTotal(data.total);
      }
    } catch (error) {
      toast.error('Failed to fetch listings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    setPage(0);
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'listings') fetchListings();
  }, [activeTab, search, statusFilter]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    else if (activeTab === 'listings') fetchListings();
  }, [page]);

  // User actions
  const openSuspendModal = (userObj, suspend) => {
    if (!suspend) {
      // Unsuspend directly
      handleUnsuspendUser(userObj);
    } else {
      setSelectedReason('');
      setAdditionalNotes('');
      setSuspendModal({ open: true, user: userObj });
    }
  };

  const handleUnsuspendUser = async (userObj) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userObj.id}/unsuspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('User unsuspended successfully');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedReason) {
      toast.error('Please select a suspension reason');
      return;
    }

    const reasonLabel = SUSPENSION_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
    const fullReason = additionalNotes 
      ? `${reasonLabel}: ${additionalNotes}`
      : reasonLabel;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${suspendModal.user.id}/suspend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: fullReason })
      });

      if (response.ok) {
        toast.success(`User suspended: ${reasonLabel}`);
        setSuspendModal({ open: false, user: null });
        setSelectedReason('');
        setAdditionalNotes('');
        fetchUsers();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Suspension failed');
      }
    } catch (error) {
      toast.error('Suspension failed');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (userObj) => {
    setSelectedReason('');
    setAdditionalNotes('');
    setDeleteModal({ open: true, user: userObj });
  };

  const handleDeleteUser = async () => {
    if (!selectedReason) {
      toast.error('Please select a deletion reason');
      return;
    }

    const reasonLabel = DELETION_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${deleteModal.user.id}?reason=${encodeURIComponent(reasonLabel)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`User deleted: ${reasonLabel}`);
        setDeleteModal({ open: false, user: null });
        setSelectedReason('');
        setAdditionalNotes('');
        fetchUsers();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  // Listing actions
  const handleRemoveListing = async (listing) => {
    const reason = prompt('Enter removal reason:');
    if (!reason) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/listings/${listing.id}/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason })
      });

      if (response.ok) {
        toast.success('Listing removed');
        fetchListings();
      }
    } catch (error) {
      toast.error('Remove failed');
    }
  };

  const handleRestoreListing = async (listing) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/listings/${listing.id}/restore`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Listing restored');
        fetchListings();
      }
    } catch (error) {
      toast.error('Restore failed');
    }
  };

  // Open listing delete modal
  const openListingDeleteModal = (listing) => {
    setListingDeleteModal({ open: true, listing: listing });
  };

  // Hard delete listing
  const handleHardDeleteListing = async () => {
    if (!listingDeleteModal.listing) return;
    
    const reason = additionalNotes || 'Admin action - permanent deletion';
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/listings/${listingDeleteModal.listing.id}?reason=${encodeURIComponent(reason)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Listing permanently deleted');
        setListingDeleteModal({ open: false, listing: null });
        setAdditionalNotes('');
        fetchListings();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Delete failed');
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
              <p className="text-zinc-400 text-sm">Manage users, listings, and platform settings</p>
            </div>
          </div>
          <button
            onClick={() => { fetchStats(); activeTab === 'users' ? fetchUsers() : fetchListings(); }}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-4">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'listings', label: 'Listings', icon: Package },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-black font-medium'
                  : 'text-zinc-400 hover:bg-zinc-800'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={stats.users.total} icon={Users} color="bg-blue-500/20 text-blue-400" subtext={`+${stats.users.new_24h} today`} />
              <StatCard title="Active Sellers" value={stats.users.active_sellers} icon={ShoppingCart} color="bg-emerald-500/20 text-emerald-400" />
              <StatCard title="Active Listings" value={stats.listings.active} icon={Package} color="bg-purple-500/20 text-purple-400" />
              <StatCard title="Total Revenue" value={`$${stats.revenue.total.toLocaleString()}`} icon={DollarSign} color="bg-amber-500/20 text-amber-400" />
              <StatCard title="Sold Listings" value={stats.listings.sold} icon={CheckCircle} color="bg-teal-500/20 text-teal-400" />
              <StatCard title="Pending Reports" value={stats.reports.pending} icon={Flag} color="bg-red-500/20 text-red-400" />
              <StatCard title="Suspended Users" value={stats.users.suspended} icon={Ban} color="bg-orange-500/20 text-orange-400" />
              <StatCard title="Disputed Orders" value={stats.orders.disputed} icon={AlertTriangle} color="bg-yellow-500/20 text-yellow-400" />
            </div>

            {/* Stripe Sync Section */}
            <StripeSyncSection 
              syncLoading={syncLoading}
              syncResult={syncResult}
              onSync={handleStripeSync}
            />
          </>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              >
                <option value="all">All Users</option>
                <option value="active">Active</option>
                <option value="seller">Sellers</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {/* Table */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr className="text-left text-xs text-zinc-500 uppercase">
                      <th className="py-3 px-4">User</th>
                      <th className="py-3 px-4">Username</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Membership</th>
                      <th className="py-3 px-4">Sales</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <UserRow
                        key={u.id}
                        user={u}
                        onView={(u) => navigate(`/profile/${u.id}`)}
                        onEdit={(u) => toast.info('Edit modal coming soon')}
                        onSuspend={openSuspendModal}
                        onDelete={openDeleteModal}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-zinc-500 text-sm">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search listings..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white"
              >
                <option value="all">All Listings</option>
                <option value="active">Active</option>
                <option value="sold">Sold</option>
                <option value="removed">Removed</option>
                <option value="reported">Reported</option>
              </select>
            </div>

            {/* Table */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-zinc-800/50">
                    <tr className="text-left text-xs text-zinc-500 uppercase">
                      <th className="py-3 px-4">Listing</th>
                      <th className="py-3 px-4">Price</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Seller</th>
                      <th className="py-3 px-4">Reports</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.map(l => (
                      <ListingRow
                        key={l.id}
                        listing={l}
                        onView={(l) => navigate(`/cards/${l.id}`)}
                        onEdit={(l) => toast.info('Edit modal coming soon')}
                        onRemove={handleRemoveListing}
                        onRestore={handleRestoreListing}
                        onHardDelete={openListingDeleteModal}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-zinc-500 text-sm">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded-lg"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Suspend User Modal */}
      <Dialog open={suspendModal.open} onOpenChange={(open) => !open && setSuspendModal({ open: false, user: null })}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
              <Ban className="w-5 h-5 text-amber-400" />
              Suspend User Account
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Suspending <strong className="text-white">{suspendModal.user?.name}</strong> (@{suspendModal.user?.username}) will deactivate their account and all listings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400 text-sm mb-2 block">Select Reason *</Label>
              <div className="space-y-2">
                {SUSPENSION_REASONS.map(reason => (
                  <label 
                    key={reason.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedReason === reason.id 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="suspendReason"
                      value={reason.id}
                      checked={selectedReason === reason.id}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="mt-1 accent-amber-500"
                    />
                    <div>
                      <p className="text-white font-medium text-sm">{reason.label}</p>
                      <p className="text-zinc-500 text-xs">{reason.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-sm mb-2 block">Additional Notes (Optional)</Label>
              <Textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                placeholder="Add any additional details..."
                className="bg-zinc-800 border-zinc-700 text-white"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setSuspendModal({ open: false, user: null })}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSuspendUser}
                disabled={!selectedReason || actionLoading}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                Suspend User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, user: null })}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete User Account
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              <span className="text-red-400 font-medium">Warning:</span> Deleting <strong className="text-white">{deleteModal.user?.name}</strong> (@{deleteModal.user?.username}) is permanent and cannot be undone. All their data and listings will be removed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400 text-sm mb-2 block">Select Reason *</Label>
              <div className="space-y-2">
                {DELETION_REASONS.map(reason => (
                  <label 
                    key={reason.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedReason === reason.id 
                        ? 'border-red-500 bg-red-500/10' 
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="deleteReason"
                      value={reason.id}
                      checked={selectedReason === reason.id}
                      onChange={(e) => setSelectedReason(e.target.value)}
                      className="mt-1 accent-red-500"
                    />
                    <div>
                      <p className="text-white font-medium text-sm">{reason.label}</p>
                      <p className="text-zinc-500 text-xs">{reason.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-400 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                This action is irreversible. The user will lose access to their account, and all associated data will be permanently deleted.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteModal({ open: false, user: null })}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={!selectedReason || actionLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete Permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hard Delete Listing Modal */}
      <Dialog open={listingDeleteModal.open} onOpenChange={(open) => !open && setListingDeleteModal({ open: false, listing: null })}>
        <DialogContent className="bg-[#18181B] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Permanently Delete Listing
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              <span className="text-red-400 font-medium">Warning:</span> You are about to permanently delete <strong className="text-white">{listingDeleteModal.listing?.player_name}</strong>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="stat-card p-3 flex items-center gap-3">
              <div className="w-14 h-16 bg-zinc-800 rounded overflow-hidden">
                {listingDeleteModal.listing?.images?.[0] ? (
                  <img src={listingDeleteModal.listing.images[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600">
                    <Package className="w-5 h-5" />
                  </div>
                )}
              </div>
              <div>
                <p className="text-white font-medium">{listingDeleteModal.listing?.player_name}</p>
                <p className="text-sm text-zinc-500">{listingDeleteModal.listing?.year} {listingDeleteModal.listing?.set_name}</p>
                <p className="text-amber-400 font-mono">${listingDeleteModal.listing?.price?.toFixed(2)}</p>
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-sm mb-2 block">Reason for Deletion (optional)</Label>
              <textarea
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                className="w-full h-20 bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-red-500"
                placeholder="Describe why this listing is being deleted..."
              />
            </div>

            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-400 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                This will permanently remove the listing from the database. The seller will not be notified.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => { setListingDeleteModal({ open: false, listing: null }); setAdditionalNotes(''); }}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleHardDeleteListing}
                disabled={actionLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete Permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
